import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getAppMaintenanceStatus } from "../../../api/system/notifications";
import { canBypassAppMaintenance, logout } from "../../../utils/auth";
import { socket } from "../../../utils/socket";

/**
 * Khóa giao diện khi Ban quản trị bật "Tắt app tạm thời".
 * Chỉ tài khoản administrator@hamesoft.com vẫn dùng app bình thường.
 */
export default function MaintenanceLockOverlay() {
  const [status, setStatus] = useState({ enabled: false });
  const canBypass = canBypassAppMaintenance();

  const applyStatus = useCallback((next) => {
    setStatus({
      enabled: Boolean(next?.enabled),
      title: next?.title || "Hệ thống đang bảo trì",
      message:
        next?.message ||
        "Vui lòng quay lại sau khi Ban quản trị mở lại ứng dụng.",
      enabled_at: next?.enabled_at || null,
    });
  }, []);

  const load = useCallback(async () => {
    const data = await getAppMaintenanceStatus();
    applyStatus(data);
  }, [applyStatus]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onSocket = (payload) => applyStatus(payload || { enabled: false });
    const onLocal = (e) => applyStatus(e?.detail || { enabled: false });
    const onApi = (e) => applyStatus(e?.detail || { enabled: true });

    socket.on("app:maintenance", onSocket);
    window.addEventListener("app-maintenance-updated", onLocal);
    window.addEventListener("app-maintenance-blocked", onApi);

    return () => {
      socket.off("app:maintenance", onSocket);
      window.removeEventListener("app-maintenance-updated", onLocal);
      window.removeEventListener("app-maintenance-blocked", onApi);
    };
  }, [applyStatus]);

  if (!status.enabled || canBypass) return null;

  return (
    <AnimatePresence>
      <motion.div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="app-maintenance-title"
        className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col"
          initial={{ scale: 0.96, y: 16 }}
          animate={{ scale: 1, y: 0 }}
        >
          <div className="px-6 py-5 bg-gradient-to-r from-amber-700 to-orange-600 text-white">
            <p className="text-[10px] font-black uppercase tracking-widest opacity-90">
              Ứng dụng tạm dừng
            </p>
            <h2 id="app-maintenance-title" className="text-xl font-bold mt-1 leading-snug">
              {status.title}
            </h2>
          </div>
          <div className="px-6 py-5 text-sm text-gray-700 whitespace-pre-wrap min-h-[100px]">
            {status.message}
          </div>
          <div className="px-6 py-4 border-t bg-gray-50 flex flex-col gap-2">
            <p className="text-center text-xs text-gray-500">
              Hệ thống sẽ mở lại khi Ban quản trị kết thúc bảo trì.
            </p>
            <button
              type="button"
              onClick={() => {
                logout();
                window.location.href = "/login";
              }}
              className="w-full py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-white transition-colors"
            >
              Đăng xuất
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
